import React, { FC, useContext, useMemo } from 'react'
import * as yup from 'yup'

import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import BookingContext from '../contexts/booking'
import Button from '@mui/material/Button'

import {
  IRecord_CRMContact_Import,
} from '@kaidam/fc-shared/src/types/schema/crm'

import {
  IData_Booking_General_Config,
} from '@kaidam/fc-shared/src/types/schema/booking'

import Form from '@kaidam/fc-frontend/src/components/form/Wrapper'
import {
  crmSchemaAll,
  crmSchemaAllPublic,
  prebookingSchemaAll,
  crmValidation,
  prebookingValidationFarAway,
  prebookingValidationClose,
  allValues,
} from '@kaidam/fc-frontend/src/components/booking/forms'

import {
  getDaysFromToday,
} from '@kaidam/fc-shared/src/booking/utils'

const BookingDetails: FC<React.PropsWithChildren<unknown>> = ({

}) => {
  const booking = useContext(BookingContext)

  const daysAway = useMemo(() => {
    if(!booking.calendarDate) return 0
    return getDaysFromToday(booking.calendarDate)
  }, [
    booking.calendarDate, 
  ])

  const initialValues = useMemo(() => {
    const crm = booking.data.details || allValues.crm
    if(!crm.postcode) {
      crm.postcode = booking.postcode
    }
    return {
      crm,
      prebooking: booking.data.prebooking || allValues.prebooking,
    }
  }, [
    allValues,
    booking.postcode,
    booking.data.details,
    booking.data.prebooking,
  ])

  const validationSchema = useMemo(() => {
    return yup.object().shape({
      crm: crmValidation,
      prebooking: daysAway >= 14 ? prebookingValidationFarAway : prebookingValidationClose,
    })
  }, [
    crmValidation,
    prebookingValidationFarAway,
    prebookingValidationClose,
    daysAway,
  ])

  const html = useMemo(() => {
    return {
      introduction: booking.generalConfig.introduction ? booking.renderLayout(booking.generalConfig.introduction) : '',
      conditions_preamble: booking.generalConfig.conditions_preamble ? booking.renderLayout(booking.generalConfig.conditions_preamble) : '',
      covid_precautions_text_preamble: booking.generalConfig.covid_precautions_text_preamble ? booking.renderLayout(booking.generalConfig.covid_precautions_text_preamble) : '',
      covid_precautions_text: booking.generalConfig.covid_precautions_text ? booking.renderLayout(booking.generalConfig.covid_precautions_text) : '',
      conditions_photos_text_preamble: booking.generalConfig.conditions_photos_text_preamble ? booking.renderLayout(booking.generalConfig.conditions_photos_text_preamble) : '',
      conditions_photos_text: booking.generalConfig.conditions_photos_text ? booking.renderLayout(booking.generalConfig.conditions_photos_text) : '',
      conditions_prep_text_preamble: booking.generalConfig.conditions_prep_text_preamble ? booking.renderLayout(booking.generalConfig.conditions_prep_text_preamble) : '',
      conditions_prep_text: booking.generalConfig.conditions_prep_text ? booking.renderLayout(booking.generalConfig.conditions_prep_text) : '',
      conditions_cancel_text_preamble: booking.generalConfig.conditions_cancel_text_preamble ? booking.renderLayout(booking.generalConfig.conditions_cancel_text_preamble) : '',
      conditions_cancel_text: booking.generalConfig.conditions_cancel_text ? booking.renderLayout(booking.generalConfig.conditions_cancel_text) : '',
      conditions_footer: booking.generalConfig.conditions_footer ? booking.renderLayout(booking.generalConfig.conditions_footer) : '',
      pricing_header: booking.generalConfig.pricing_header ? booking.renderLayout(booking.generalConfig.pricing_header) : '',
      pricing_footer: booking.generalConfig.pricing_footer ? booking.renderLayout(booking.generalConfig.pricing_footer) : '',
      
    }
  }, [
    booking.generalConfig,
    booking.renderLayout,
  ])

  if(!booking.initialised || !booking.calendarDate) return null

  return (
    <Box sx={{ display: 'flex', p: 2 }}>
      <Form
        validationSchema={ validationSchema }         
        error={ "" }
        initialValues={ initialValues }
        handlers={{
          disabled: (props) => {
            if(props.name == 'crm.postcode') return true
            return false
          },
        }}
        onSubmit={ (data) => {
          booking.update({
            details: data.crm as IRecord_CRMContact_Import,
            prebooking: data.prebooking as Record<string, any>,
          })
          booking.openPage('payment')
        } }
      >
        {
          ({
            render,
            setFieldValue,
            values,
            showErrors,
            errors,
            isValid,
            onSubmit,
          }) => {
            return (
              <Box>
                <Box sx={{ mt: 2, mb: 2 }} dangerouslySetInnerHTML={{__html: html.introduction}} />
                <Typography
                  align="left"
                  gutterBottom
                  variant="h6"
                  sx={{ mt: 3 }}
                >
                  Your details
                </Typography>
                {
                  render(crmSchemaAllPublic)
                }
                {
                  render(prebookingSchemaAll)
                }
                <Typography
                  align="left"
                  gutterBottom
                  variant="h6"
                  sx={{ mt: 3 }}
                >
                  Covid Precautions
                </Typography>
                <Grid container spacing={ 2 }>
                  <Grid item xs={ 12 }>
                    <div dangerouslySetInnerHTML={{__html: html.covid_precautions_text_preamble}} />
                  </Grid>
                  <Grid item xs={ 1 }>
                    <Checkbox
                      checked={ values.prebooking.covid_precautions }
                      onChange={ (e) => {
                        setFieldValue('prebooking.covid_precautions', e.target.checked)
                      }}
                    />
                  </Grid>
                  <Grid item xs={ 11 }>
                    <div dangerouslySetInnerHTML={{__html: html.covid_precautions_text}} />
                  </Grid>
                </Grid>
                <Typography
                  align="left"
                  gutterBottom
                  variant="h6"
                  sx={{ mt: 3 }}
                >
                  Survey Terms & Conditions
                </Typography>
                <Grid container spacing={ 2 }>
                  <Grid item xs={ 12 }>
                    <div dangerouslySetInnerHTML={{__html: html.conditions_prep_text_preamble}} />
                  </Grid>

                  <Grid item xs={ 1 }>
                    <Checkbox
                      checked={ values.prebooking.conditions_prep }
                      onChange={ (e) => {
                        setFieldValue('prebooking.conditions_prep', e.target.checked)
                      }}
                    />
                  </Grid>
                  <Grid item xs={ 11 }>
                    <div dangerouslySetInnerHTML={{__html: html.conditions_prep_text}} />
                  </Grid>
                  <Typography
                    align="left"
                    gutterBottom
                    variant="h6"
                    sx={{ mt: 3 }}
                  >
                    Collection & Use of Data/Photos
                  </Typography>
                  <Grid item xs={ 12 }>
                    <div dangerouslySetInnerHTML={{__html: html.conditions_photos_text_preamble}} />
                  </Grid>
                  <Grid item xs={ 1 }>
                    <Checkbox
                      checked={ values.prebooking.conditions_photos }
                      onChange={ (e) => {
                        setFieldValue('prebooking.conditions_photos', e.target.checked)
                      }}
                    />
                  </Grid>
                  <Grid item xs={ 11 }>
                    <div dangerouslySetInnerHTML={{__html: html.conditions_photos_text}} />
                  </Grid>
                  {
                    daysAway < 14 && (
                      <>
                        <Typography
                          align="left"
                          gutterBottom
                          variant="h6"
                          sx={{ mt: 3 }}
                        >
                          Cancellation Policy
                        </Typography>
                        <Grid item xs={ 12 }>
                          <div dangerouslySetInnerHTML={{__html: html.conditions_cancel_text_preamble}} />
                        </Grid>
                        <Grid item xs={ 1 }>
                          <Checkbox
                            checked={ values.prebooking.conditions_cancel }
                            onChange={ (e) => {
                              setFieldValue('prebooking.conditions_cancel', e.target.checked)
                            }}
                          />
                        </Grid>
                        <Grid item xs={ 11 }>
                          <div dangerouslySetInnerHTML={{__html: html.conditions_cancel_text}} />
                        </Grid>
                      </>
                    )
                  }
                  <Grid item xs={ 12 }>
                    <div dangerouslySetInnerHTML={{__html: html.conditions_footer}} />
                  </Grid>
                </Grid>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2 }}
                  onClick={ onSubmit }
                >
                  Next
                </Button>
              </Box>
            )
          }
        }               
      </Form>
    </Box>
  )
}

export default BookingDetails